import { convertObjectToCamelCase, convertObjectToSnakeCase } from '@/helpers'
import {
  InvalidateQueryFilters,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  RouteListWithPagesProps,
  RouteOriginListWithPagesProps,
  TransporCompanyRoutesListWithPagesProps,
  TransportCompanyListWithPagesProps,
  TransportCompanyProps,
  VehicleListWithPagesProps,
} from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'
import {
  BFF_LOGISTIC_ROUTES_URL,
  BFF_LOGISTIC_TRANSPORT_COMPANIES_URL,
  BFF_LOGISTIC_TRANSPORT_COMPANY_ROUTES_URL,
  BFF_LOGISTIC_VEHICLES_URL,
  LOGISTIC_ROUTE_ORIGINS_URL
} from './api.urls'

const transportCompanyRoutesPermittedParams: string[] = [
  'limit',
  'offset',
  'ordering',
  'search',
  'currency',
  'logisticRoute_DestinationLocation',
  'logisticRoute_Name',
  'logisticRoute_originLocation',
  'logisticRoute_Uuid',
  'transportCompany_Email',
  'transportCompany_Name',
  'transportCompany_Uuid',
  'type',
  'vehicle_Capacity',
  'vehicle_Name',
  'vehicle_Slug',
  'vehicle_Uuid',
  'isActive',
]

const transportCompanyPermittedParams: string[] = [
  'limit',
  'offset',
  'ordering',
  'search',
  'name',
  'email',
  'type',
  'isActive',
]

const routesPermittedParams: string[] = [
  'limit',
  'offset',
  'ordering',
  'search',
  'name',
  'originLocation',
  'destinationLocation',
  'isForeign',
]

const vehiclesPermittedParams: string[] = [
  'limit',
  'offset',
  'ordering',
  'search',
]

export const useTransportCompanyList = (
  {
    params,
  }: {
    params?: Record<string, string | number | boolean>
  },
  isEnabled = true,
) => {
  const getTransportCompanyList = async (): Promise<{
    success: boolean
    message: string
    data?: TransportCompanyListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && transportCompanyPermittedParams.includes(key)) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_LOGISTIC_TRANSPORT_COMPANIES_URL, {
        params: {
          ...(formattedParams as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Transport Companies fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as TransportCompanyListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get transport companies!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.TRANSPORT_COMPANY.module, params],
    queryFn: () => getTransportCompanyList(),
    enabled: isEnabled,
  })
}

export const useTransportCompanyDetail = (uuid?: string) => {
  const getTransportCompanyDetail = async (): Promise<{
    success: boolean
    message: string
    data?: TransportCompanyProps
  }> => {
    try {
      const { data } = await api.get(`${BFF_LOGISTIC_TRANSPORT_COMPANIES_URL}${uuid}/`)

      return {
        success: true,
        message: 'Transport Company fetched successfully!',
        data: convertObjectToCamelCase(data) as TransportCompanyProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get transport company!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.TRANSPORT_COMPANY.module, uuid],
    queryFn: () => getTransportCompanyDetail(),
    enabled: !!uuid,
  })
}

export const useCreateTransportCompany = () => {
  const queryClient = useQueryClient()
  const createTransportCompany = async (
    payload: Partial<TransportCompanyProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: TransportCompanyProps
  }> => {
    try {
      const { data } = await api.post(
        BFF_LOGISTIC_TRANSPORT_COMPANIES_URL,
        payload,
      )

      return {
        success: true,
        message: 'Transport Company created successfully!',
        data: convertObjectToCamelCase(data) as TransportCompanyProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to create transport company!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.TRANSPORT_COMPANY.module],
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERIES_ADMIN.TRANSPORT_COMPANY.module,
      ] as InvalidateQueryFilters)
    },
    mutationFn: (payload: Partial<TransportCompanyProps>) =>
      createTransportCompany(payload),
  })
}

export const useUpdateTransportCompany = () => {
  const queryClient = useQueryClient()
  const updateTransportCompany = async (
    payload: Partial<TransportCompanyProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: TransportCompanyProps
  }> => {
    try {
      const { data } = await api.patch(
        `${BFF_LOGISTIC_TRANSPORT_COMPANIES_URL}${payload.uuid}/`,
        { ...payload, uuid: undefined },
      )

      return {
        success: true,
        message: 'Transport Company updated successfully!',
        data: convertObjectToCamelCase(data) as TransportCompanyProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update transport company!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.TRANSPORT_COMPANY.module],
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERIES_ADMIN.TRANSPORT_COMPANY.module,
      ] as InvalidateQueryFilters)
    },
    mutationFn: (payload: Partial<TransportCompanyProps>) =>
      updateTransportCompany(payload),
  })
}

export const useRouteList = ({
  params,
  isEnabled = true,
}: {
  params?: Record<string, string | number>
  isEnabled?: boolean
}) => {
  const getRouteList = async (): Promise<{
    success: boolean
    message: string
    data?: RouteListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && routesPermittedParams.includes(key)) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_LOGISTIC_ROUTES_URL, {
        params: {
          ...(formattedParams as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Routes fetched successfully!',
        data: convertObjectToCamelCase(data) as RouteListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get transport routes!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ROUTE.module, params],
    queryFn: () => getRouteList(),
    enabled: isEnabled,
  })
}

export const useVehiclesList = ({
  params,
  isEnabled = true
}: {
  params?: Record<string, string | number>
  isEnabled?: boolean
}) => {
  const getVehiclesList = async (): Promise<{
    success: boolean
    message: string
    data?: VehicleListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && vehiclesPermittedParams.includes(key)) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_LOGISTIC_VEHICLES_URL, {
        params: {
          ...(formattedParams as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Vehicles fetched successfully!',
        data: convertObjectToCamelCase(data) as VehicleListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get transport vehicle!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.VEHICLES.module, params],
    queryFn: () => getVehiclesList(),
    enabled: isEnabled,
  })
}

export const useRoutesOriginList = ({
  params,
  isEnabled = true
}: {
  params?: Record<string, string | number>
  isEnabled?: boolean
}) => {
  const getRoutesOriginList = async (): Promise<{
    success: boolean
    message: string
    data?: RouteOriginListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(LOGISTIC_ROUTE_ORIGINS_URL, {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'RoutesOrigins fetched successfully!',
        data: convertObjectToCamelCase(data) as RouteOriginListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get origins!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ROUTE_ORIGIN.module, params],
    queryFn: () => getRoutesOriginList(),
    enabled: isEnabled,
  })
}

export const useTransporCompanyRoutesList = ({
  params,
}: {
  params?: Record<string, string | number>
}) => {
  const getTransporCompanyRoutesList = async (): Promise<{
    success: boolean
    message: string
    data?: TransporCompanyRoutesListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && transportCompanyRoutesPermittedParams.includes(key)) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_LOGISTIC_TRANSPORT_COMPANY_ROUTES_URL, {
        params: {
          ...(formattedParams as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Transport Companies Routes fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as TransporCompanyRoutesListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get transport companies routes!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.TRANSPORT_COMPANY_ROUTE.module, params],
    queryFn: () => getTransporCompanyRoutesList(),
  })
}
